import { useContextGR } from '@google-recaptcha';
import { useEventEmailCaptured } from '@google-tag-manager';
import useTranslation from 'next-translate/useTranslation';
import { useForm } from 'react-hook-form';
import { object, string } from 'yup';

import { SubscribeService } from '@core/api';
import {
  Box,
  ButtonSubmit,
  FormControl,
  Image,
  InputTextControl,
  Typography,
} from '@core/component';
import { Translate } from '@core/constant';
import { useContextUtil } from '@core/context';
import { useLanguage, useValidator } from '@core/hook';
import { getStaticPathIconFolder } from '@core/media';
import { EGTMCaptureLocation, ETypographyVariant } from '@core/type';

import { FormProps, InputForm } from './interface-modal-spotlight';

const schema = object({
  email: string().email().required(),
});

export const Form = ({ onCloseModal, onSuccess }: FormProps) => {
  const { pushEventEmailCaptured } = useEventEmailCaptured();
  const { axiosInstance } = useContextUtil();
  const { language } = useLanguage();
  const { getRecaptcha } = useContextGR();

  const { t: tProduct } = useTranslation(Translate.common.PRODUCT);
  const { t: tForm } = useTranslation(Translate.common.FORM);

  const { control, handleSubmit, trigger, reset, setError } = useForm({
    defaultValues: {
      email: '',
    },
    resolver: useValidator(schema, tForm),
    mode: 'onChange',
  });

  return (
    <FormControl
      t={tForm}
      trigger={trigger}
      reset={reset}
      handleSubmit={handleSubmit}
      setError={setError}
      fn={async (data: InputForm) => {
        const token = await getRecaptcha();
        const submitData = {
          ...data,
          token,
        };
        return SubscribeService.subscribeEmail(axiosInstance, language, submitData).then(() => {
          onCloseModal();
          onSuccess();
          pushEventEmailCaptured(EGTMCaptureLocation.NEWSLETTER);
        });
      }}
    >
      <Box display="flex" justifyContent="center">
        <Image
          src={getStaticPathIconFolder('spotlight/ic-subscribe.svg')}
          blurDataURL={getStaticPathIconFolder('spotlight/ic-subscribe.svg')}
          width="72"
          height="72"
          alt={tProduct('subscribe.subscribe.alt')}
        />
      </Box>
      <Box marginTop="24px" marginBottom="12px">
        <Typography fontStyle="italic" variant={ETypographyVariant.H3} textAlign="center">
          {tProduct('getSpotlight')}
        </Typography>
      </Box>
      <Typography variant={ETypographyVariant.BODY2} textAlign="center" marginBottom="24px">
        {tProduct('subscribe.description')}
      </Typography>

      <Box margin={'20px auto'} width={'100%'} maxWidth={'362px'}>
        <InputTextControl control={control} name="email" label={tForm('email.placeholder')} />
      </Box>

      <Box display="flex" justifyContent="center">
        <ButtonSubmit>{tProduct('subscribe.buttonSubscribe')}</ButtonSubmit>
      </Box>
    </FormControl>
  );
};
